// Utkastlagring för annonsflödet.
// Bilder i base64 blir snabbt för stora för localStorage (~5 MB), vilket gjorde att
// hela utkastet tystnade bort. Därför lagras utkastet i IndexedDB med localStorage
// som fallback (utan bilder) för äldre utkast.

const DB_NAME = "sellavo";
const STORE = "drafts";
const KEY = "utkast";
export const DRAFT_LEGACY_KEY = "sellavo:utkast";
export const DRAFT_ID_KEY = "sellavo:utkast-id";
/** Tidigare nycklar – läses en sista gång så påbörjade utkast inte tappas. */
export const DRAFT_LEGACY_KEY_OLD = "saljavo:utkast";
const DRAFT_ID_KEY_OLD = "saljavo:utkast-id";

/** Unikt id för det pågående utkastet i den här webbläsaren. Skapas vid behov. */
export function getDraftId(): string {
  try {
    let id =
      window.localStorage.getItem(DRAFT_ID_KEY) ?? window.localStorage.getItem(DRAFT_ID_KEY_OLD);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36);
      window.localStorage.setItem(DRAFT_ID_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

export type DraftVideo = { path: string; name: string; poster: string | null };

/** Ett föremål som väntar på en egen annons efter att annonsen delats upp. */
export type PendingGroup<TImage = unknown> = {
  label: string;
  title: string;
  description: string;
  price: string;
  category: string;
  tags: string[];
  images: TImage[];
};

/** Bildstudions arbetsläge: originalbilder och sparade AI-versioner per bild. */
export type StoredStudio<TImage = unknown> = {
  /** Bildindex → originalbilden innan AI rörde den. */
  originals: Record<string, TImage>;
  /** "index:läge:variant" → färdig AI-version. */
  results: Record<string, TImage>;
  /** "index:läge" → senaste variantnummer. */
  variants: Record<string, number>;
  /** Bildindex → valt läge. */
  modes: Record<string, string>;
};

export type StoredDraft<TForm = unknown, TImage = unknown> = {
  id?: string;
  /** Kontot som utkastet tillhör. null = påbörjat utan inloggning. */
  ownerId?: string | null;
  form: TForm;
  images: TImage[];
  video?: DraftVideo | null;
  pendingGroups?: PendingGroup<TImage>[];
  /** AI-taggar för matchning – måste följa med så de inte tappas vid omladdning. */
  tags?: string[];
  /** Bildstudio: original + versioner så att Ångra alltid fungerar efter omladdning. */
  studio?: StoredStudio<TImage> | null;
  savedAt: number;
};


function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function saveDraft<TForm, TImage>(
  form: TForm,
  images: TImage[],
  video?: DraftVideo | null,
  pendingGroups?: PendingGroup<TImage>[],
  ownerId?: string | null,
  tags?: string[],
  studio?: StoredStudio<TImage> | null,
): Promise<boolean> {
  const payload: StoredDraft<TForm, TImage> = {
    id: getDraftId(),
    ownerId: ownerId ?? null,
    form,
    images,
    video: video ?? null,
    pendingGroups: pendingGroups ?? [],
    studio: studio ?? null,

    tags: tags ?? [],
    savedAt: Date.now(),
  };
  const db = await openDb();
  if (db) {
    const ok = await new Promise<boolean>((resolve) => {
      try {
        const tx = db.transaction(STORE, "readwrite");
        tx.objectStore(STORE).put(payload, KEY);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
        tx.onabort = () => resolve(false);
      } catch {
        resolve(false);
      }
    });
    db.close();
    if (ok) {
      // Håll en liten kopia utan bilder så att andra flikar ser att ett utkast finns.
      try {
        window.localStorage.setItem(
          DRAFT_LEGACY_KEY,
          JSON.stringify({
            id: payload.id,
            ownerId: payload.ownerId,
            form,
            images: [],
            video: video ? { path: video.path, name: video.name, poster: null } : null,
            tags: tags ?? [],
            savedAt: payload.savedAt,
          }),
        );
      } catch {
        /* ignorera */
      }
      return true;
    }
  }

  try {
    window.localStorage.setItem(DRAFT_LEGACY_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

/**
 * Ett utkast får bara användas av den det tillhör.
 * Utkast utan ägare (påbörjade utloggad) adopteras av den som loggar in.
 */
function ownerMatches(draftOwner: string | null | undefined, userId: string | null): boolean {
  const owner = draftOwner ?? null;
  if (owner === null) return true;
  return owner === userId;
}

export async function loadDraft<TForm, TImage>(
  userId: string | null = null,
): Promise<StoredDraft<TForm, TImage> | null> {
  const db = await openDb();
  if (db) {
    const value = await new Promise<StoredDraft<TForm, TImage> | null>((resolve) => {
      try {
        const tx = db.transaction(STORE, "readonly");
        const req = tx.objectStore(STORE).get(KEY);
        req.onsuccess = () => resolve((req.result as StoredDraft<TForm, TImage>) ?? null);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    db.close();
    if (value?.images?.length) {
      if (!ownerMatches(value.ownerId, userId)) {
        await clearDraft();
        return null;
      }
      return value;
    }
  }
  try {
    const raw =
      window.localStorage.getItem(DRAFT_LEGACY_KEY) ??
      window.localStorage.getItem(DRAFT_LEGACY_KEY_OLD);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft<TForm, TImage>;
    if (!parsed?.images?.length) return null;
    if (!ownerMatches(parsed.ownerId, userId)) {
      await clearDraft();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export async function clearDraft(): Promise<void> {
  const db = await openDb();
  if (db) {
    try {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(KEY);
    } catch {
      /* ignorera */
    }
    db.close();
  }
  try {
    window.localStorage.removeItem(DRAFT_LEGACY_KEY);
    window.localStorage.removeItem(DRAFT_LEGACY_KEY_OLD);
    window.localStorage.removeItem(DRAFT_ID_KEY);
  } catch {
    /* ignorera */
  }
}

/** Sant om ett utkast (även utan bilder) finns i den här webbläsaren. */
export function hasLocalDraftHint(): boolean {
  try {
    const raw =
      window.localStorage.getItem(DRAFT_LEGACY_KEY) ??
      window.localStorage.getItem(DRAFT_LEGACY_KEY_OLD);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { form?: { title?: string } };
    return Boolean(parsed?.form);
  } catch {
    return false;
  }
}
